/**
 * PreviewModeIndicator — subtle gold dot in the corner that reminds you
 * when demo-mode is active (splashes/onboarding/alignment/ritual all
 * suppressed). Tap it to see what's currently bypassed.
 *
 * To turn off demo mode for production, edit /app/frontend/src/App.js
 * and set:
 *   showSplash → true (default initial state)
 *   ritualChecked / alignmentChecked → false (so checks run)
 *   showOnboarding → false (auto-driven by user state)
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ACTIVE = true; // flip to false to hide the dot entirely

export default function PreviewModeIndicator() {
  const [open, setOpen] = useState(false);
  if (!ACTIVE) return null;

  return (
    <div
      className="fixed top-3 right-3 z-[9999]"
      style={{ pointerEvents: "auto" }}
      data-testid="preview-mode-indicator"
    >
      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.9 }}
        className="relative w-3 h-3 rounded-full"
        style={{
          background: "#CBA153",
          boxShadow: "0 0 10px rgba(203,161,83,0.7), 0 0 18px rgba(203,161,83,0.4)",
        }}
        aria-label="Preview mode active"
      >
        <motion.span
          className="absolute inset-0 rounded-full"
          style={{ background: "#CBA153" }}
          animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-6 rounded-xl px-4 py-3 w-64"
            style={{
              background: "rgba(15,18,24,0.95)",
              border: "1px solid rgba(203,161,83,0.35)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            <p
              className="text-[10px] uppercase tracking-[0.3em] mb-2"
              style={{ color: "#CBA153" }}
            >
              Preview Mode
            </p>
            <p className="text-white/85 text-sm mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Demo flags active
            </p>
            <ul className="text-white/55 text-[11px] leading-relaxed space-y-0.5">
              <li>· Splash screen — off</li>
              <li>· Onboarding — off</li>
              <li>· Alignment Check — off</li>
              <li>· Daily Ritual — off</li>
              <li>· Excelior auto-pop — off</li>
            </ul>
            <p className="text-white/30 text-[10px] mt-2 italic">
              Toggle in <span className="text-white/45">App.js</span> before production.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
